import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { useXR } from "@react-three/xr";
import * as THREE from "three";
import XrMenu, { MenuInteractionContext, MenuTarget } from "./XrMenu";
import { readFrame, watchSample } from "./frameInput";
import {
  freshWatch, stepWatch, isRaisedMenuPose, isHandRaised, wristMenuWalkedAway, chooseSource, freshPoke, stepPoke,
  freshSelection, startSelection, consumeSelection, canCommitSelection,
  isEffectivelyVisible, validTeleport, teleportOffset, hasHandInput,
} from "./interactionPolicy";

const PANEL_OFFSET = new THREE.Vector3(0, 0.11, 0);
const UP = new THREE.Vector3(0, 1, 0);
const FORWARD = new THREE.Vector3(0, 0, 1);

export default function XrControls({ settings, seeThrough, onCarOnlyChange, onPowertrainChange }) {
  const session = useXR((state) => state.session);
  const player = useXR((state) => state.player);
  const gl = useThree((state) => state.gl);
  const { nodes } = useGLTF("/models/tunnel.glb");
  const targets = useMemo(() => new Map(), []);
  const panel = useRef();
  const launcher = useRef();
  const floor = useRef();
  const pointer = useRef();
  const cursor = useRef();
  const marker = useRef();
  const [presentation, setPresentation] = useState(null);
  const [handsAvailable, setHandsAvailable] = useState(() => hasHandInput(session?.inputSources));
  const [fallbackAvailable, setFallbackAvailable] = useState(false);
  const [feedback, setFeedback] = useState({ hover: null, pressed: null });
  const props = useRef();
  props.current = { settings, onCarOnlyChange };
  const runtime = useRef({
    data: null, mode: null, watch: freshWatch(), leftSource: null,
    selection: freshSelection(), pokes: new Map(), previousTip: null, tipSource: null,
    grip: null, teleportCapture: null, fallbackSince: null, fallback: false,
    anchored: false, placed: false, pressedUntil: 0, pressedId: null,
  });
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const lineGeometry = useMemo(() => new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, 0, -1)]), []);
  useEffect(() => () => lineGeometry.dispose(), [lineGeometry]);

  const highlight = (hover, pressed = null) => setFeedback((old) => old.hover === hover && old.pressed === pressed ? old : { hover, pressed });
  const cancel = () => {
    const state = runtime.current;
    state.selection = freshSelection(); state.pokes.clear(); state.previousTip = null; state.tipSource = null;
    state.teleportCapture = null; state.grip = null;
    state.pressedId = null; state.pressedUntil = 0;
    if (marker.current) marker.current.visible = false;
    if (pointer.current) pointer.current.visible = false;
    if (cursor.current) cursor.current.visible = false;
    highlight(null);
  };
  const present = (mode) => {
    if (runtime.current.mode === mode) return;
    cancel(); runtime.current.mode = mode; runtime.current.placed = false;
    // React unmount removes targets; this also disables them within this XR frame.
    if (panel.current) panel.current.visible = false;
    setPresentation(mode);
  };
  const close = () => {
    runtime.current.watch = { ...freshWatch(), suppressed: true };
    present(null);
  };
  const currentTargets = () => Array.from(targets.values()).filter((target) => isEffectivelyVisible(target.object));
  const cast = (source) => {
    if (!source?.tracked || !source.ray) return null;
    raycaster.set(source.ray.position, source.ray.direction);
    const candidates = currentTargets();
    candidates.forEach((target) => target.object.updateWorldMatrix(true, false));
    const hit = raycaster.intersectObjects(candidates.map((target) => target.object), false)[0];
    return hit ? { ...hit, target: candidates.find((target) => target.object === hit.object) } : null;
  };
  const directTargets = (source) => {
    const tip = source?.joints?.["index-finger-tip"]?.position;
    if (!tip || source.kind !== "hand") return [];
    return currentTargets().map((target) => {
      target.object.updateWorldMatrix(true, false);
      const point = target.object.worldToLocal(tip.clone());
      const scale = target.object.getWorldScale(new THREE.Vector3());
      const normal = FORWARD.clone().applyQuaternion(target.object.getWorldQuaternion(new THREE.Quaternion()));
      return { target, normal, depth: point.z * scale.z, inside: Math.abs(point.x) <= target.width / 2 && Math.abs(point.y) <= target.height / 2 };
    }).filter((hit) => hit.inside && hit.depth >= -0.02 && hit.depth <= 0.05).sort((a, b) => Math.abs(a.depth) - Math.abs(b.depth));
  };
  const canUseSource = (source) => {
    if (!source?.tracked) return false;
    if (source.inputSource.handedness === "right") return true;
    // World fallback can be operated with the only available hand or gaze source.
    return runtime.current.mode !== "wrist" && runtime.current.mode !== "controller" &&
      !chooseSource(runtime.current.data?.inputs ?? [], "right");
  };
  const floorHit = (source) => {
    const state = runtime.current;
    if (hasHandInput(session?.inputSources) || source?.kind !== "controller" || !floor.current || !source?.tracked || !canUseSource(source)) return null;
    if (source.kind === "controller" && state.grip !== source.inputSource) return null;
    raycaster.set(source.ray.position, source.ray.direction);
    floor.current.updateWorldMatrix(true, false);
    const hit = raycaster.intersectObject(floor.current, false)[0];
    return validTeleport(hit?.point, state.data?.head, {
      enabled: props.current.settings.teleportEnabled, menuOpen: !!state.mode,
      focused: session?.visibilityState === "visible", ageMs: performance.now() - (state.data?.time ?? -Infinity),
    }) ? hit : null;
  };
  const activate = (target) => {
    if (targets.get(target.id) !== target || !isEffectivelyVisible(target.object)) return;
    runtime.current.selection = consumeSelection(runtime.current.selection);
    runtime.current.teleportCapture = null;
    runtime.current.pressedId = target.id;
    runtime.current.pressedUntil = performance.now() + 160;
    highlight(target.id, target.id);
    target.activate();
  };

  // Native events are handled once for all inputs. There is no parallel XR5
  // Interactive handler or synthetic joint-pinch event to duplicate selection.
  const handlers = useRef();
  handlers.current = {
    selectstart(event) {
      const source = eventSource(event);
      if (!canUseSource(source)) { cancel(); return; }
      const direct = directTargets(source).length > 0;
      const hit = cast(source);
      runtime.current.selection = startSelection(event.inputSource, hit?.target ?? null, direct);
      runtime.current.teleportCapture = !hit && !direct && floorHit(source) ? event.inputSource : null;
      highlight(hit?.target.id ?? null, hit?.target.id ?? null);
    },
    select(event) {
      const state = runtime.current;
      const source = eventSource(event);
      if (!canUseSource(source)) { cancel(); return; }
      const direct = directTargets(source).length > 0;
      const hit = cast(source);
      if (canCommitSelection(state.selection, event.inputSource, hit?.target, direct)) activate(hit.target);
      else if (!hit && !direct && !state.selection.target && !state.selection.consumed && state.teleportCapture === event.inputSource) {
        const destination = floorHit(source);
        if (destination) {
          player.position.add(teleportOffset(destination.point, state.data.head));
          player.updateMatrixWorld(true);
          state.anchored = false;
        }
      }
      state.selection = consumeSelection(state.selection);
      state.teleportCapture = null;
    },
    selectend() {
      // select usually precedes selectend; do not reset the consumed latch here.
      // It is replaced only by a new selectstart, regardless of event ordering.
    },
    squeezestart(event) {
      const source = eventSource(event);
      if (source?.kind !== "controller" || !source.grip) return;
      if (event.inputSource.handedness === "left") present(runtime.current.mode === "controller" ? null : "controller");
      else if (event.inputSource.handedness === "right" && props.current.settings.teleportEnabled && !hasHandInput(session?.inputSources) && !runtime.current.mode) runtime.current.grip = event.inputSource;
    },
    squeezeend(event) {
      if (runtime.current.grip === event.inputSource) { runtime.current.grip = null; runtime.current.teleportCapture = null; }
    },
    visibilitychange() { cancel(); runtime.current.watch = freshWatch(); present(null); },
    inputsourceschange() { cancel(); runtime.current.watch = freshWatch(); present(null); },
    end() { cancel(); present(null); },
  };
  function eventSource(event) {
    // An input event carries its own fresh XRFrame, even between slow render
    // frames. Read both the viewer and source from that same frame.
    const data = readFrame(event.frame, session, gl.xr.getReferenceSpace(), player);
    if (!data) return null;
    runtime.current.data = data;
    const left = chooseSource(data.inputs, "left");
    if (runtime.current.mode === "wrist" && runtime.current.placed && wristMenuWalkedAway(data.head, panel.current?.position)) {
      runtime.current.watch = stepWatch(runtime.current.watch, { walkedAway: true });
      present(null);
    }
    if (runtime.current.mode === "controller" && (left?.kind !== "controller" || !left.grip)) {
      runtime.current.watch = freshWatch();
      present(null);
    }
    return data.inputs.find((source) => source.inputSource === event.inputSource) ?? null;
  }
  useEffect(() => {
    if (!session) return;
    runtime.current.watch = freshWatch(); runtime.current.anchored = false;
    const names = ["selectstart", "select", "selectend", "squeezestart", "squeezeend", "visibilitychange", "inputsourceschange", "end"];
    const listeners = names.map((name) => { const listener = (event) => handlers.current[name](event); session.addEventListener(name, listener); return [name, listener]; });
    return () => listeners.forEach(([name, listener]) => session.removeEventListener(name, listener));
  }, [session]);

  useFrame((_, delta, frame) => {
    const state = runtime.current;
    const data = readFrame(frame, session, gl.xr.getReferenceSpace(), player);
    state.data = data;
    if (!data) {
      cancel();
      if (state.mode !== "wrist") { state.watch = freshWatch(); present(null); }
      return;
    }
    const available = hasHandInput(session?.inputSources);
    if (available !== handsAvailable) setHandsAvailable(available);
    if (available) { state.teleportCapture = null; state.grip = null; }
    const now = performance.now();
    const left = chooseSource(data.inputs, "left");
    const right = chooseSource(data.inputs, "right");
    const source = right ?? (state.mode !== "wrist" && state.mode !== "controller" ? left ?? data.inputs.find((input) => input.tracked) : null);
    const sample = watchSample(left, data);
    if (state.leftSource !== left?.inputSource) {
      state.leftSource = left?.inputSource;
      if (state.mode !== "wrist") { state.watch = freshWatch(); if (state.mode === "controller") present(null); }
    }
    state.watch = stepWatch(state.watch, {
      tracked: !!sample, pose: !!sample && isRaisedMenuPose(sample), raised: !!sample && isHandRaised(sample), now,
      walkedAway: state.mode === "wrist" && state.placed && wristMenuWalkedAway(data.head, panel.current?.position),
    });
    if (state.watch.open && state.mode !== "wrist") present("wrist");
    if (state.mode === "wrist" && !state.watch.open) present(null);
    if (state.mode === "controller" && (!left?.grip || left.kind !== "controller")) present(null);
    const hasLeftMenu = !!sample || (left?.kind === "controller" && !!left.grip);
    if (hasLeftMenu) state.fallbackSince = null;
    else if (state.fallbackSince === null) state.fallbackSince = now;
    const fallback = state.mode !== "wrist" && !hasLeftMenu && now - state.fallbackSince >= 750;
    if (fallback !== state.fallback) {
      state.fallback = fallback;
      if (fallback) state.anchored = false;
      setFallbackAvailable(fallback);
    }

    if (!state.anchored && launcher.current) {
      const direction = data.forward.clone().setY(0).normalize();
      launcher.current.position.copy(data.head).addScaledVector(direction, 0.8); launcher.current.position.y -= 0.16;
      launcher.current.quaternion.setFromRotationMatrix(new THREE.Matrix4().lookAt(data.head, launcher.current.position, UP));
      state.anchored = true;
    }
    if (panel.current && state.mode) {
      let position, rotation;
      if (state.mode === "wrist" && (state.placed || sample)) {
        // Capture a world-space pose once. The raised hand gates visibility but
        // neither wrist motion nor viewer motion moves an already-open panel.
        position = state.placed ? panel.current.position : sample.wrist.clone().add(PANEL_OFFSET);
        rotation = state.placed ? panel.current.quaternion : new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().lookAt(data.head, position, UP));
      } else if (state.mode === "controller" && left?.grip) {
        position = new THREE.Vector3(0.1, 0.145, -0.135).applyMatrix4(left.grip.matrix);
        rotation = left.grip.rotation.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.98, 0.18, -0.24)));
      } else if (state.mode === "world" && launcher.current) {
        position = launcher.current.position; rotation = launcher.current.quaternion;
      }
      if (position) {
        if (state.mode !== "wrist" || !state.placed) {
          const alpha = state.placed ? 1 - Math.exp(-delta * 18) : 1;
          panel.current.position.lerp(position, alpha); panel.current.quaternion.slerp(rotation, alpha);
        }
        panel.current.visible = true; panel.current.updateMatrixWorld(true); state.placed = true;
      } else panel.current.visible = false;
    }
    // Losing the selecting source invalidates all captured input immediately.
    if (state.tipSource !== source?.inputSource) { cancel(); state.tipSource = source?.inputSource; }
    if (!source?.tracked) { cancel(); return; }
    const tip = source.joints?.["index-finger-tip"]?.position;
    const direct = directTargets(source);
    const near = direct[0];
    if (near) state.selection = consumeSelection(state.selection);
    const currentIds = new Set(direct.map((hit) => hit.target.id));
    for (const id of state.pokes.keys()) if (!currentIds.has(id)) state.pokes.delete(id);
    let poked = false;
    for (const contact of direct) {
      const advance = tip && state.previousTip ? -tip.clone().sub(state.previousTip).dot(contact.normal) : 0;
      const result = stepPoke(state.pokes.get(contact.target.id) ?? freshPoke(), { inside: true, depth: contact.depth, advance });
      state.pokes.set(contact.target.id, result.state);
      if (result.activate && !poked) { activate(contact.target); poked = true; }
    }
    state.previousTip = tip?.clone() ?? null;
    if (!tip) state.pokes.clear();
    const hit = near ? null : cast(source);
    const destination = !near && !hit ? floorHit(source) : null;
    const pressed = now < state.pressedUntil ? state.pressedId : state.selection.source === source.inputSource && !state.selection.consumed ? state.selection.target?.id : null;
    highlight(near?.target.id ?? hit?.target.id ?? null, pressed);
    if (pointer.current) {
      pointer.current.visible = !near && (!!state.mode || !!hit || !!destination);
      pointer.current.position.copy(source.ray.position); pointer.current.quaternion.copy(source.ray.rotation);
      pointer.current.scale.z = hit?.distance ?? destination?.distance ?? 0.6;
    }
    if (cursor.current) {
      cursor.current.visible = !!near || !!hit;
      if (near && tip) cursor.current.position.copy(tip);
      else if (hit) cursor.current.position.copy(hit.point);
    }
    if (marker.current) {
      marker.current.visible = !!destination;
      if (destination) { marker.current.position.copy(destination.point); marker.current.position.y += 0.004; }
    }
    if (!destination) state.teleportCapture = null;
    if (!data.inputs.some((input) => input.tracked && input.inputSource === state.grip)) state.grip = null;
  });

  const context = useMemo(() => ({ targets, feedback }), [targets, feedback]);
  return (
    <MenuInteractionContext.Provider value={context}>
      <group ref={panel} name="xr-menu-panel" visible={false} scale={presentation === "wrist" ? 0.5 : 1}>
        {presentation && <XrMenu key={presentation} carOnly={settings.carOnly} powertrain={settings.powertrain} seeThrough={seeThrough} onCarOnlyChange={(value) => { cancel(); onCarOnlyChange(value); }} onPowertrainChange={(value) => { cancel(); onPowertrainChange(value); }} onClose={close} />}
      </group>
      <group ref={launcher} name="xr-menu-launcher">
        {fallbackAvailable && !presentation && <MenuTarget id="open-menu" width={0.13} height={0.05} label="MENU" onActivate={() => present("world")} />}
      </group>
      {settings.teleportEnabled && !handsAvailable && !presentation && <group rotation-y={Math.PI / 2}>
        <mesh ref={floor} name="xr-teleport-floor" geometry={nodes.shadowPlane.geometry}>
          <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} side={THREE.DoubleSide} />
        </mesh>
      </group>}
      <line ref={pointer} geometry={lineGeometry} visible={false} frustumCulled={false} renderOrder={50}>
        <lineBasicMaterial color="#ffffff" transparent opacity={0.7} depthTest={false} toneMapped={false} />
      </line>
      <mesh ref={cursor} visible={false} renderOrder={51}>
        <sphereGeometry args={[0.004, 12, 8]} />
        <meshBasicMaterial color="#ffffff" depthTest={false} toneMapped={false} />
      </mesh>
      <mesh ref={marker} visible={false} rotation-x={-Math.PI / 2}>
        <ringGeometry args={[0.16, 0.23, 48]} />
        <meshBasicMaterial color="#eb0a1e" side={THREE.DoubleSide} toneMapped={false} />
      </mesh>
    </MenuInteractionContext.Provider>
  );
}
